import { useEffect, useRef, useState } from 'react'
import { CameraOff, Flashlight, FlashlightOff, Loader2 } from 'lucide-react'
import { t } from '@/i18n'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  SCAN_REGION,
  createRepeatFilter,
  loadCodeDetector,
  regionInVideo,
  type CodeDetector,
} from '@/lib/cameraScanner'

const DETECT_INTERVAL_MS = 120
const READ_FLASH_MS = 400

type Status = 'loading' | 'scanning' | 'denied' | 'unavailable' | 'error' | 'decoderError'

interface CameraScannerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Devuelve true si el código sirvió: la cámara se cierra. Con false sigue leyendo. */
  onCode: (code: string) => boolean | Promise<boolean>
}

/**
 * Lee de a un código: mientras `onCode` resuelve el anterior no toma otro, y
 * se cierra sola cuando uno sirvió.
 */
export function CameraScannerDialog({ open, onOpenChange, onCode }: CameraScannerDialogProps) {
  const [video, setVideo] = useState<HTMLVideoElement | null>(null)
  const [status, setStatus] = useState<Status>('loading')
  const [lastCode, setLastCode] = useState<string | null>(null)
  const [torchAvailable, setTorchAvailable] = useState(false)
  const [torchOn, setTorchOn] = useState(false)
  const [flashing, setFlashing] = useState(false)
  const trackRef = useRef<MediaStreamTrack | null>(null)
  const onCodeRef = useRef(onCode)
  onCodeRef.current = onCode
  const onOpenChangeRef = useRef(onOpenChange)
  onOpenChangeRef.current = onOpenChange

  useEffect(() => {
    if (!open || !video) return
    let cancelled = false
    let stream: MediaStream | null = null
    let timer: number | undefined
    let flashTimer: number | undefined
    const repeatFilter = createRepeatFilter()
    // Solo se lee lo que está dentro del recuadro (más un margen).
    const frame = document.createElement('canvas')
    const frameContext = frame.getContext('2d', { willReadFrequently: true })

    const captureRegion = () => {
      if (!frameContext || !video.videoWidth || !video.clientWidth) return null
      const area = regionInVideo(
        { width: video.videoWidth, height: video.videoHeight },
        { width: video.clientWidth, height: video.clientHeight }
      )
      if (frame.width !== area.width) frame.width = area.width
      if (frame.height !== area.height) frame.height = area.height
      frameContext.drawImage(video, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height)
      return frame
    }

    setStatus('loading')
    setLastCode(null)
    setTorchAvailable(false)
    setTorchOn(false)

    const tick = async (detector: CodeDetector) => {
      if (cancelled) return
      const region = video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA ? captureRegion() : null
      if (region) {
        try {
          const [barcode] = await detector.detect(region)
          const code = barcode?.rawValue.trim()
          if (!cancelled && code && repeatFilter.accept(code, performance.now())) {
            setLastCode(code)
            setFlashing(true)
            window.clearTimeout(flashTimer)
            flashTimer = window.setTimeout(() => setFlashing(false), READ_FLASH_MS)
            // El próximo cuadro se lee recién cuando este código se resolvió.
            const done = await onCodeRef.current(code)
            // Lo que tardó no cuenta como fuera de cuadro: si sigue a la vista, no se repite.
            repeatFilter.accept(code, performance.now())
            if (done && !cancelled) {
              onOpenChangeRef.current(false)
              return
            }
          }
        } catch {
          // Un cuadro que no se pudo leer no detiene el escaneo.
        }
      }
      if (!cancelled) timer = window.setTimeout(() => void tick(detector), DETECT_INTERVAL_MS)
    }

    void (async () => {
      try {
        const opened = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        })
        // Si se cerró mientras se pedía permiso, la limpieza ya pasó sin stream.
        if (cancelled) {
          opened.getTracks().forEach((track) => track.stop())
          return
        }
        stream = opened
        video.srcObject = stream
        await video.play()
        const track = stream.getVideoTracks()[0]
        trackRef.current = track
        const capabilities = track.getCapabilities?.() as
          | { torch?: boolean; focusMode?: string[] }
          | undefined
        setTorchAvailable(!!capabilities?.torch)
        // Con el enfoque fijo un código de cerca sale borroso y no se lee.
        if (capabilities?.focusMode?.includes('continuous')) {
          track
            .applyConstraints({ advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet] })
            .catch(() => {})
        }
      } catch (error) {
        if (cancelled) return
        const name = error instanceof DOMException ? error.name : ''
        if (name === 'NotAllowedError' || name === 'SecurityError') setStatus('denied')
        else if (name === 'NotFoundError' || name === 'OverconstrainedError') setStatus('unavailable')
        else setStatus('error')
        return
      }

      try {
        const detector = await loadCodeDetector()
        if (cancelled) return
        setStatus('scanning')
        void tick(detector)
      } catch {
        if (!cancelled) setStatus('decoderError')
      }
    })()

    return () => {
      cancelled = true
      window.clearTimeout(timer)
      window.clearTimeout(flashTimer)
      setFlashing(false)
      // Sin esto la cámara queda encendida (y su luz prendida) al cerrar.
      stream?.getTracks().forEach((track) => track.stop())
      trackRef.current = null
      video.srcObject = null
    }
  }, [open, video])

  const toggleTorch = async () => {
    const track = trackRef.current
    if (!track) return
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn } as MediaTrackConstraintSet] })
      setTorchOn(!torchOn)
    } catch {
      setTorchAvailable(false)
    }
  }

  const failed = status !== 'loading' && status !== 'scanning'
  const message = {
    loading: t('pos.cameraLoading'),
    scanning: lastCode ? t('pos.cameraLastRead', { code: lastCode }) : t('pos.cameraHint'),
    denied: t('pos.cameraDenied'),
    unavailable: t('pos.cameraUnavailable'),
    error: t('pos.cameraError'),
    decoderError: t('pos.cameraDecoderError'),
  }[status]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>{t('pos.cameraScan')}</DialogTitle>
          <DialogDescription className="sr-only">{t('pos.cameraHint')}</DialogDescription>
        </DialogHeader>

        <div className="relative aspect-[4/3] sm:aspect-video overflow-hidden rounded-md bg-black">
          <video
            ref={setVideo}
            className="h-full w-full object-cover"
            playsInline
            muted
            autoPlay
            aria-hidden="true"
          />
          {status === 'scanning' && (
            <div
              className={cn(
                'pointer-events-none absolute rounded-md border-2 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)] transition-colors',
                flashing ? 'border-green-400' : 'border-white/80'
              )}
              style={{
                left: `${SCAN_REGION.x * 100}%`,
                top: `${SCAN_REGION.y * 100}%`,
                width: `${SCAN_REGION.width * 100}%`,
                height: `${SCAN_REGION.height * 100}%`,
              }}
              aria-hidden="true"
            >
              <div className="absolute inset-x-2 top-1/2 h-px -translate-y-1/2 bg-red-500/70" />
            </div>
          )}
          {status === 'loading' && (
            <Loader2 className="absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 animate-spin text-white" />
          )}
          {failed && (
            <CameraOff className="absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 text-white/70" />
          )}
        </div>

        <p
          className={failed ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}
          role="status"
          aria-live="polite"
        >
          {message}
        </p>

        <DialogFooter className="flex-row gap-2 sm:justify-between">
          {torchAvailable && status === 'scanning' ? (
            <Button
              variant="outline"
              className="h-12"
              onClick={() => void toggleTorch()}
              aria-pressed={torchOn}
            >
              {torchOn ? <FlashlightOff className="h-4 w-4 mr-2" /> : <Flashlight className="h-4 w-4 mr-2" />}
              {torchOn ? t('pos.cameraTorchOff') : t('pos.cameraTorchOn')}
            </Button>
          ) : (
            <span />
          )}
          <Button className="h-12 flex-1 sm:flex-none" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
